import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '@/lib/cx';
import { Badge } from './Badge';
import { RowCheckbox } from './RowCheckbox';
import { Tooltip } from './Tooltip';

export interface MultiComboboxOption {
  value: string;
  label: string;
  /** Secondary line — a designation, a country, anything that disambiguates duplicates. */
  hint?: string;
}

export interface MultiComboboxProps {
  id?: string;
  values: string[];
  onChange: (values: string[]) => void;
  options: MultiComboboxOption[];
  /** Shown when nothing is selected. */
  placeholder?: string;
  /** Placeholder inside the filter box. */
  searchPlaceholder?: string;
  loading?: boolean;
  /** Rendered under the list — e.g. "Showing the first 200 of 400". */
  footnote?: string;
  disabled?: boolean;
  invalid?: boolean;
  /**
   * The selected values a server actually refused, so each offending chip is
   * marked rather than only the control as a whole.
   *
   * This exists because the API names *every* bad id in one 422 — a line-item
   * write refusing `Cities do not exist or are not active: 4, 9` is telling the
   * form which chips to point at, deliberately, so the user fixes all of them
   * in one pass instead of resubmitting to discover the next one. Reddening the
   * whole box and listing the names in the error text underneath throws that
   * away: the reader has to map names back onto chips by eye.
   *
   * Values not currently selected are ignored, so a stale refusal left over
   * from a previous submit cannot mark a chip that is no longer there.
   *
   * Non-empty implies `invalid` for the control's own border and `aria-invalid`
   * — a refused selection *is* an invalid control — but the two are independent
   * otherwise, and `invalid` alone behaves exactly as it always has.
   *
   * Additive: omitted behaves exactly as before.
   */
  invalidValues?: readonly string[];
  /**
   * How many selections the trigger lists as chips before it counts the rest
   * instead. `0` lists none of them, so the trigger is always a single count —
   * which is what a narrow table cell wants: four chips in a ~190px cell wrap
   * to four rows and take the whole grid row with them.
   *
   * This is the cap that has always existed, exposed rather than a new mode:
   * the rule "list this many, then count the remainder" degenerates at `0` to
   * "count everything", so nothing above needed a second code path.
   *
   * Defaults to `MAX_VISIBLE_CHIPS`, so every existing call site is unchanged.
   */
  maxVisibleChips?: number;
  /**
   * The noun the count reads with: `{singular: 'city', plural: 'cities'}` gives
   * "1 city" / "4 cities". It names the *selection*, so it also becomes the
   * trigger's accessible name — a screen reader hearing "4 cities" learns more
   * than one hearing "4 selected", and in count-only mode the visible text is
   * the only other place that information exists.
   *
   * Defaults to the wording the trigger has always used, so passing nothing
   * leaves both the visible and the announced text byte-identical.
   */
  summaryNoun?: { singular: string; plural: string };
  /** Hard cap; the API rejects more than 50 per role. */
  maxSelected?: number;
  /**
   * A pinned row at the TOP of the list that creates a new option instead of
   * choosing one — the multi-select twin of `PickerField`'s "+ Add a new…"
   * option, for the masters whose missing row can be added without a migration.
   *
   * It is a ROW rather than a button beside the list because that is the only
   * place a keyboard reaches: `Tab` inside the popover closes it, so anything
   * outside the listbox would be pointer-only. Arrow keys, Home/End and Enter
   * therefore walk it as index 0 and everything below it shifts down — see
   * `pinnedRows` below, which is the whole of the arithmetic. ⚠️ It shares that
   * offset with the select-all row, which is pinned UNDER it: this row keeps
   * index 0 either way.
   *
   * `onSelect` is called with the popover already closed and focus back on the
   * trigger, so a dialog opened from it records the trigger as the element to
   * restore focus to. It must NOT toggle a selection; nothing is selected by
   * pressing it, and the caller adds the created id to `values` itself.
   *
   * ⚠️ The row is BLOCKED at `maxSelected`, exactly as an unselected option is:
   * a value that cannot then be selected is a dead end, and the cap note below
   * the list already says why.
   *
   * Additive: omitted renders no row and leaves every index and key press
   * exactly as it was.
   */
  addNew?: {
    /** Written like an action, e.g. `+ Add a new city…`. */
    label: string;
    onSelect: () => void;
  };
  /**
   * A pinned row UNDER `addNew` that ticks every VISIBLE option at once, and
   * reads "Clear all" and unticks exactly those once they are all selected.
   *
   * A ROW rather than a button beside the list, for the reason `addNew` gives:
   * `Tab` inside the popover closes it, so anything outside the listbox would
   * be pointer-only and no keyboard could reach it.
   *
   * ⛔ **OPT-IN, AND DELIBERATELY NOT UNIVERSAL — this control is mounted on
   * every multi-select in the app, and an audit of those mounts found four
   * populations where a select-all is wrong rather than merely unhelpful.**
   * Omitted renders no row and leaves every index, key press and call site
   * byte-identical.
   *
   * 1. **IT WOULD CONTRADICT THE `footnote` IT SITS BESIDE.** There is no
   *    `onSearch` anywhere in this app: filtering is local over already-loaded
   *    `options`, so "visible" can never exceed the loaded page. Several mounts
   *    render a truncation footnote today — "Showing the first 100 of {total}",
   *    and the project picker, where `perPage` is 100 against 113 active
   *    projects. On each of those a "Select all" asserts exactly what the line
   *    under it denies, on day one, by construction.
   *    ⚠️ **Narrowed 2026-09-13 to the EDIT mounts among them**: on a FILTER
   *    there is no assertion to contradict, because nothing is stored and the
   *    trigger counts rather than claiming "all" — see `'even-when-truncated'`
   *    below. The `/quotes` and `/invoices` PROJECT FILTERS are that case
   *    exactly, and are why the value exists; a project picker on an edit form
   *    is still squarely this bullet.
   * 2. **SOME MOUNTS WRITE LIVE, WHOLE-SET, ON EVERY TOGGLE, with named
   *    irreversible consequences.** A company's projects of interest: adding one
   *    CREATES a sales pursuit and removing one closes that pursuit as LOST,
   *    which re-ticking does not reopen — so one press could create ~100
   *    pursuits and the obvious undo would close every one of them permanently.
   *    (A server-side guard against exactly that mass gesture was argued for and
   *    refused; the client's diff against the set it opened with is the only
   *    guard.) User-group membership PATCHes on every toggle, `[]` clears it,
   *    and that field gates 422s on five endpoints. A line's cities are stored
   *    in array ORDER and that order is what Xero prints on the customer's
   *    document.
   * 3. **SOME SIT AN INCH BELOW A PURPOSE-BUILT "All regions" CHECKBOX** whose
   *    meaning is deliberately different — *the project's declared regions*, not
   *    *everything in this list*. A second, similar-looking control beneath it
   *    is a mis-click hazard, not an improvement.
   * 4. **SOME DEFINE EMPTY AS ALL**, so selecting everything is semantically a
   *    no-op that writes a large explicit set in place of the intended empty
   *    one.
   *
   * ⛔ **`true` RENDERS NOTHING WHENEVER `footnote` IS PRESENT, EVEN IF YOU
   * PASSED IT — and that original ruling is UNCHANGED and STILL BINDING for
   * every EDIT field.** Owner's ruling: a Select all must never appear on a
   * list that cannot reach its whole population, and the interlock is enforced
   * HERE rather than left to thirty call sites to remember. On this control
   * `footnote` is in practice always a truncation report — its own doc gives
   * "Showing the first 200 of 400" as the example — and with no `onSearch`
   * anywhere in this app, "visible" can never exceed the loaded page. A caller
   * passing both is asking for a control that would assert exactly what the
   * line beneath it denies, so the component refuses instead of obeying.
   *
   * ⚠️ **The degradation is SILENCE, not an error**: pass `true` alongside a
   * footnote and you get no row, with nothing failing anywhere. That is
   * deliberate — the alternative is a control that lies — but it does mean a
   * future caller can be surprised by it, and this comment is where they find
   * out why.
   *
   * ⚠️ **If a truncated EDIT list ever genuinely needs one, the answer is
   * PAGING THE OPTIONS IN, never relaxing this rule.** Reaching the whole
   * population is the precondition, not a nicety.
   *
   * ### `'even-when-truncated'` — FILTERS ONLY (owner's carve-out, 2026-09-13)
   *
   * The rule above is materially weaker on a FILTER, and the owner has ruled it
   * should not apply there. **`'even-when-truncated'` renders the row beside a
   * footnote**; `true` keeps the interlock exactly as it was, and absent /
   * `false` is unchanged.
   *
   * ⚠️ **The harm the interlock prevents is a STORED SET, and a filter does not
   * store one.** Ticking the 100 options that loaded out of 113 on an edit
   * field writes a set claiming to be complete, onto a document a customer is
   * billed against — the set outlives the popover and nothing on it ever says
   * it was short. A filter's selection is a NARROWING, evaluated now and
   * discarded: it selects less than the user might have meant, which shows up
   * immediately as a list that is missing rows, and is corrected by picking
   * again. **The trigger also reads a COUNT — "100 projects" — never the word
   * "all"**, so nothing on screen claims completeness in the first place, and
   * the truncation footnote stays visible underneath saying exactly how short
   * the list is. The live case: the `/quotes` and `/invoices` project filters,
   * where `perPage` is 100 against 113 active projects, so the row drew nothing
   * at all.
   *
   * ⛔ **DO NOT PASS IT ON AN EDIT FIELD.** On a line-item cities or campaign
   * inclusions picker it buys a stored set that silently omits whatever the
   * server never sent — inclusions print on the customer's invoice and a line's
   * cities are stored in array ORDER and pushed to Xero — with a `200`, no
   * refusal, and nothing anywhere reporting that the set was assembled from a
   * truncated list. That is the exact failure the interlock exists for, and it
   * is why this is a filters-only value rather than a relaxation of the rule.
   *
   * ⚠️ **A string literal rather than a second boolean prop, deliberately.** It
   * cannot be passed by accident the way a `selectAllEvenWhenTruncated` beside
   * `selectAll` could, it reads at the call site as exactly what it does, and
   * it keeps ONE prop answering "does this list offer select-all" instead of
   * two that can disagree — there is no state here where a caller has said both
   * yes and no.
   *
   * ⛔ **The two labels live in THIS component and are deliberately not the
   * caller's.** A per-call-site label prop would let thirty mounts word one
   * control thirty ways — the failure this repo already records against helper
   * text and against client-side pluralisation. One copy of the wording.
   *
   * ⚠️ **Where it IS switched on — under EITHER value — it acts on the LOADED
   * and FILTERED set and never on the whole population** — see
   * `allVisibleSelected` below.
   */
  selectAll?: boolean | 'even-when-truncated';
  'aria-describedby'?: string;
}

const POPOVER_MAX_HEIGHT = 288;

/**
 * The popover never narrows below this however narrow the trigger is — a ~100px
 * table cell would otherwise open a ~100px list of truncated names.
 */
const POPOVER_MIN_WIDTH = 240;

/** How close to the viewport edge the popover may sit, on either side. */
const VIEWPORT_MARGIN = 8;

/**
 * Beyond this many selections the trigger stops listing every chip and shows a
 * count instead. Twelve names already wrap to four rows at 320px; past that the
 * trigger stops being a control and becomes a list.
 */
const MAX_VISIBLE_CHIPS = 8;

/**
 * "4 selected" is what the trigger has always announced, so it is what the
 * count says when a caller names no noun — the prop can then be added to one
 * call site without moving the wording under any of the others.
 *
 * Both forms are the caller's to supply and NEITHER is derived. `city` →
 * `cities` follows no rule worth writing, and the first thing a client-side
 * pluraliser does is print "1 cities"; `src/lib/inclusions.ts` states the same
 * rule for the wording that reaches a customer's invoice, and this control
 * sits directly above those lines.
 */
const DEFAULT_SUMMARY_NOUN = { singular: 'selected', plural: 'selected' } as const;

const TABBABLE =
  'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"]),[contenteditable="true"]';

/**
 * Moves focus to the tabbable element after (or, with `backward`, before) the
 * trigger, as a native `<select>` would on Tab. Returns false when there is
 * nowhere to go, so the caller can park focus on the trigger instead.
 *
 * ⚠️ The popover is portalled to the end of `<body>`, so a Tab left to the
 * browser continued from the filter box and wrapped to the skip link (or, on
 * Shift+Tab, the last control on the page) — measured 2026-10-11. Inside a modal
 * the walk is scoped to the dialog and wraps, because the caller stops the Tab
 * from reaching `Modal`'s own trap.
 *
 * A byte-for-byte twin of the one in `Combobox.tsx`; the two must move together.
 */
function focusPastTrigger(
  trigger: HTMLElement | null,
  popover: HTMLElement | null,
  backward: boolean,
): boolean {
  if (!trigger) return false;
  const modal = trigger.closest<HTMLElement>('[aria-modal="true"]');
  const scope = modal ?? document.body;
  const items = Array.from(scope.querySelectorAll<HTMLElement>(TABBABLE)).filter(
    (el) =>
      el === trigger ||
      (el.tabIndex >= 0 &&
        !popover?.contains(el) &&
        !el.closest('[inert]') &&
        el.getClientRects().length > 0),
  );
  const index = items.indexOf(trigger);
  if (index === -1) return false;
  let target = backward ? items[index - 1] : items[index + 1];
  if (!target && modal) target = backward ? items.at(-1) : items[0];
  if (!target || target === trigger) return false;
  target.focus();
  return true;
}

function ChevronIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4 shrink-0 text-fg-subtle"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4 shrink-0 text-fg-subtle"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-3 w-3"
    >
      <path d="M6 6 18 18M18 6 6 18" />
    </svg>
  );
}

/**
 * The non-colour half of a refused chip. Sized by the caller, because the chip
 * and the list row sit at different scales and merging two `h-*` utilities
 * through `cx` would leave both in the class list.
 */
function AlertIcon({ className = 'h-3 w-3' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cx('shrink-0', className)}
    >
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  );
}

/**
 * A searchable multi-select — `Combobox`'s sibling, deliberately built to the
 * same shape so the two read as one control in a form.
 *
 * Differences that matter:
 *
 * - Picking an option **toggles** it and leaves the popover open. Choosing six
 *   people is the normal case here; closing after each one would make it a
 *   six-round trip.
 * - Selections render as removable chips in the trigger, which therefore grows
 *   in height rather than scrolling sideways. At 320px the chips wrap. A caller
 *   with no vertical room to give — a table cell — passes `maxVisibleChips={0}`
 *   and gets a single count instead; removal then happens in the popover, which
 *   is where it was always possible.
 * - A chosen option is marked by a LEADING checkbox, built to match the filter
 *   popover's option rows (`ColumnFilter`) so the two read as one idea. It is
 *   deliberately non-interactive — see `RowCheckbox`, where the reason is the
 *   listbox's keyboard model rather than styling.
 * - `selectAll` pins a select-all row that toggles every VISIBLE option — the
 *   loaded, filtered set, never the whole population. Opt-in, and at `true`
 *   ⛔ **inert alongside a `footnote`**, which on this control always reports
 *   truncation: on an EDIT field the two would be contradictory claims about
 *   one list, and the row would store a set claiming to be complete.
 *   `'even-when-truncated'` is the owner's FILTERS-ONLY carve-out, where the
 *   selection is a narrowing rather than a stored set. Read the prop.
 * - `maxSelected` disables the *unselected* options at the cap and says why.
 *   Silently inert rows read as a broken picker. The select-all row is blocked
 *   whenever selecting all would overshoot the cap — never partially applied.
 * - `addNew` pins an "+ Add a new…" row at the top of the list — the same flow
 *   `PickerField` gives the single-select deal pickers, for a master whose
 *   missing row can be created without a migration. It is navigable as index 0
 *   and selects nothing; the caller adds the created id to `values`.
 * - `invalidValues` marks individual chips, for the servers here that name
 *   every offending id in one refusal rather than the first. A marked chip
 *   carries three signals — tone, a dashed edge and an alert glyph — plus a
 *   screen-reader marker, so none of it depends on seeing the red.
 *
 * As in `Combobox`, the popover is portalled and positioned `fixed`. That is
 * load-bearing, not tidiness: these pickers live inside a modal whose body is
 * `overflow-y-auto`, which would clip an inline popover.
 *
 * A value with no matching option still renders as a chip, labelled with the
 * raw value, so it can always be removed. Callers holding snapshotted names for
 * ids that have left their source directory should merge them into `options` so
 * the chip shows a name (see `RepresentativesField`).
 */
export function MultiCombobox({
  id,
  values,
  onChange,
  options,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  loading = false,
  footnote,
  disabled = false,
  invalid = false,
  invalidValues,
  maxVisibleChips = MAX_VISIBLE_CHIPS,
  summaryNoun = DEFAULT_SUMMARY_NOUN,
  maxSelected,
  addNew,
  selectAll = false,
  'aria-describedby': describedBy,
}: MultiComboboxProps) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [rect, setRect] = useState<{
    left: number;
    top: number;
    // Bounds for content-sizing the popover (`width: max-content`) — see
    // `place()` below.
    minWidth: number;
    maxWidth: number;
    flip: boolean;
  }>({
    left: 0,
    top: 0,
    minWidth: 0,
    maxWidth: 0,
    flip: false,
  });

  const listId = useId();
  const capId = `${listId}-cap`;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const byValue = useMemo(
    () => new Map(options.map((option) => [option.value, option])),
    [options],
  );

  // Chips follow the order of `values`, not of `options`: the caller's order is
  // the only one the user can predict.
  const chips = useMemo(
    () => values.map((value) => ({ value, label: byValue.get(value)?.label ?? value })),
    [values, byValue],
  );

  // Intersected with `values` rather than trusted as given: a refusal held in
  // form state outlives the selection it named, and a caller that clears the
  // chip without clearing the error must not leave the count claiming a mark
  // that is nowhere on screen. `null` rather than an empty Set so every read
  // below is one truthiness check.
  const refused = useMemo(() => {
    if (invalidValues === undefined || invalidValues.length === 0) return null;
    const selected = new Set(values);
    const marked = new Set(invalidValues.filter((value) => selected.has(value)));
    return marked.size === 0 ? null : marked;
  }, [invalidValues, values]);

  const refusedCount = refused?.size ?? 0;
  // A refused selection is an invalid control, so the box marks itself without
  // the caller having to pass `invalid` as well. It may still pass it — the two
  // are independent, and `invalid` on its own is unchanged.
  const isInvalid = invalid || refusedCount > 0;

  // No `onSearch` here — every consumer loads its whole directory in one page,
  // so filtering is local and a server round-trip would only add latency.
  const visible = useMemo(() => {
    const needle = term.trim().toLowerCase();
    if (needle === '') return options;
    return options.filter((option) =>
      `${option.label} ${option.hint ?? ''}`.toLowerCase().includes(needle),
    );
  }, [options, term]);

  const atCap = maxSelected !== undefined && values.length >= maxSelected;

  /*
   * The offset the PINNED rows impose on every option index, and the total
   * number of NAVIGABLE rows.
   *
   * Two rows can sit above the options, in this order:
   *
   *   0              the "add a new…" row, when `addNew` is given
   *   addNewRows     the select-all row, when `selectAll` survives its gate
   *   pinnedRows +i  option `i`
   *
   * ⚠️ **`addNew` KEEPS INDEX 0 AND ITS `${listId}-0` ID.** Its own doc explains
   * why it is first: it is the row somebody reaches for when the list matches
   * nothing, which is exactly when they have typed a term that matches nothing.
   *
   * Kept as an offset rather than sentinel values spliced into `options`: a
   * sentinel has to be a string that no caller's real value can ever be, which
   * is a promise this control cannot make about ids it never sees. An offset of
   * 0 collapses to exactly the old arithmetic when neither row is offered.
   *
   * ⚠️ **EVERY ONE OF THESE READS THE OFFSET** — `rowCount`, `active`,
   * `activeOption`, the two `*IsActive` tests, the Home/End/Arrow handler
   * (through `rowCount`), `aria-activedescendant` (through `active`) and each
   * row's own `id`. **Grep `pinnedRows` rather than counting them here.**
   * Missing one shifts the highlight off the row the user is on, silently:
   * nothing errors and the list still renders.
   *
   * The "add a new…" row is rendered while `loading` too, deliberately — so its
   * index is a constant for as long as the popover is open, and a keyboard
   * position cannot shift under the user when a slow list arrives. The
   * select-all row follows `visible.length` for the same reason rather than
   * `loading`: every consumer here loads its whole directory in one page, so a
   * non-empty `visible` under `loading` is not a state these callers reach.
   */
  const addNewRows = addNew ? 1 : 0;
  /*
   * ⛔ **THE `footnote` INTERLOCK, ENFORCED HERE AND NOT AT THE CALL SITES.**
   * A truncation footnote and a "Select all" are contradictory claims about the
   * same list, so passing both yields NO ROW — silently, by design. The prop's
   * own doc carries the ruling and the reason the degradation is silence.
   *
   * ✅ **THAT RULING IS UNCHANGED AND STILL BINDING FOR `selectAll === true`,
   * WHICH IS EVERY EDIT FIELD.** Ticking the 100 options that loaded out of 113
   * stores a set claiming to be complete, on a document a customer is billed
   * against.
   *
   * 🔴 **`selectAll === 'even-when-truncated'` OPTS OUT — owner's carve-out,
   * 2026-09-13, FOR FILTERS ONLY.** The harm above is a STORED SET; a filter's
   * selection is a NARROWING that is evaluated and discarded, its trigger reads
   * a COUNT ("100 projects") rather than the word "all", and the truncation
   * footnote stays on screen beside it saying how short the list is. Nothing
   * claims completeness, so there is no contradiction to interlock against.
   * ⛔ Passing it on a line-item cities or inclusions picker buys exactly the
   * stored, silently-short set this interlock exists to prevent — read the
   * prop's doc before using it.
   *
   * Also hidden outright when nothing is visible, under BOTH values: with no
   * rows to act on it would be a dead control sitting above "No matches", and
   * at the cap a blocked row above an empty list explains nothing.
   */
  // `!footnote`, matching the footnote's own render gate below rather than
  // testing `undefined`: an empty string renders no footnote, so it must not
  // suppress this row either, or the two disagree about whether one is there.
  const selectAllSurvivesFootnote = selectAll === 'even-when-truncated' || !footnote;
  const selectAllRows = selectAll && selectAllSurvivesFootnote && visible.length > 0 ? 1 : 0;
  const pinnedRows = addNewRows + selectAllRows;
  const rowCount = visible.length + pinnedRows;

  /*
   * ⚠️ **THE SELECT-ALL ROW ACTS ON THE LOADED AND FILTERED SET — NEVER ON THE
   * WHOLE POPULATION.** Two narrowings could sit under it, and only one of them
   * survives:
   *
   * - **The filter term, which does** — so a user who typed "Aus" and pressed
   *   it must not silently select things they cannot see. `ColumnFilter` scopes
   *   its own Select all / Clear pair to the visible subset for the same
   *   reason. Nothing is written under the row to say so: the no-helper-text
   *   prohibition binds, and the row's own toggle to "Clear all" is what tells
   *   a reader which set it is talking about.
   * - **Truncation, which DOES under `selectAll === 'even-when-truncated'`.**
   *   ⚠️ *SUPERSEDED 2026-09-13: "**Truncation, which CANNOT** — the row does
   *   not render at all beside a `footnote`, so the 'Showing the first 200 of
   *   400' case never reaches here."* That is still exactly right for
   *   `selectAll === true`, which is every EDIT field; the owner's filters-only
   *   carve-out lets a FILTER render the row beside its footnote, so on those
   *   mounts "every visible option" really is the loaded page and not the
   *   population. **That is the accepted reading there** — a filter narrows,
   *   the trigger counts rather than saying "all", and the footnote stays on
   *   screen underneath. See `selectAllRows` below for the interlock and the
   *   prop for both rulings.
   */
  const allVisibleSelected = useMemo(
    () => visible.length > 0 && visible.every((option) => values.includes(option.value)),
    [visible, values],
  );

  /*
   * ⛔ **BLOCKED, NEVER PARTIAL.** Selecting "as many as fit" would leave a
   * selection that reports itself as "all" while being short — the silent wrong
   * answer this codebase refuses everywhere — so the row is blocked exactly as
   * an unselected option is blocked at the cap. Clearing stays available at the
   * cap, since removing is how a user gets out of it.
   *
   * Counted as a merged SET rather than `values.length + visible.length`: the
   * two overlap, and adding them would block a row that would have fitted.
   */
  const selectAllWouldTotal = useMemo(() => {
    const merged = new Set(values);
    for (const option of visible) merged.add(option.value);
    return merged.size;
  }, [values, visible]);
  const selectAllBlocked =
    !allVisibleSelected && maxSelected !== undefined && selectAllWouldTotal > maxSelected;
  /*
   * A disabled control that cannot say why is a bug, not a default — and the
   * cap note under the list only renders once the cap is actually reached, so
   * it does not cover the case where selecting all would OVERSHOOT a cap the
   * selection has not yet hit. The sentence is routed rather than dropped: to
   * `title`, and to an `sr-only` span on the row, which is the same pair a
   * refused option row already uses.
   */
  const selectAllBlockedReason = selectAllBlocked
    ? `Selecting all ${visible.length} would take the selection past the limit of ${maxSelected}.`
    : undefined;

  const place = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const box = el.getBoundingClientRect();
    const below = window.innerHeight - box.bottom;
    const flip = below < POPOVER_MAX_HEIGHT + 16 && box.top > below;
    // Clamped horizontally as well as flipped vertically. The popover is
    // `fixed`, so a right-hand overhang adds no page scroll and those options
    // are silently unreachable. Real case: the quote grid's Cities cell, a
    // ~101px content box at `md` forced up to POPOVER_MIN_WIDTH, opening near
    // x≈620 and ending near 860 on a 768px viewport.
    //
    // Shifting left is preferred to narrowing, because the width floor exists
    // so labels stay readable; width is only reduced when the viewport itself
    // cannot hold the floor, at which point the popover spans it margin to
    // margin.
    //
    // The popover sizes to its CONTENT (`width: max-content` in the portal
    // below), so long option labels stop truncating just because the trigger
    // cell is narrow — the owner asked for these pickers to "have full width
    // to show full names". `minWidth`/`maxWidth` are the bounds; the content
    // decides the actual width anywhere in between.
    //
    // ⚠️ Must MEASURE that rendered width rather than assume it, and must stay
    // idempotent — it reruns on every scroll, resize and selection change.
    // Reading the live width here converges every call on one answer. Before
    // the first render there is nothing to measure, so `minWidth` is the
    // fallback — never an underestimate that pushes the popover off-screen —
    // and the effect below re-runs `place()` once the real box exists.
    const minWidth = Math.max(box.width, POPOVER_MIN_WIDTH);
    const maxWidth = Math.max(window.innerWidth - VIEWPORT_MARGIN * 2, 0);
    const measured = popoverRef.current?.getBoundingClientRect().width ?? 0;
    const width = Math.min(Math.max(measured, minWidth), maxWidth);

    setRect((prev) => {
      const left = Math.max(
        VIEWPORT_MARGIN,
        // `innerWidth`, matching the vertical logic's `innerHeight` above rather
        // than introducing a second notion of "the viewport" in one function.
        Math.min(box.left, window.innerWidth - VIEWPORT_MARGIN - width),
      );
      const top = flip ? box.top : box.bottom;
      if (
        prev.left === left &&
        prev.top === top &&
        prev.minWidth === minWidth &&
        prev.maxWidth === maxWidth &&
        prev.flip === flip
      ) {
        return prev;
      }
      return { left, top, minWidth, maxWidth, flip };
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    place();
  }, [open, place]);

  // The trigger sits inside a scrolling modal, and it also changes height as
  // chips wrap — both have to move the popover, so re-place on selection too.
  useEffect(() => {
    if (!open) return;
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, place]);

  useLayoutEffect(() => {
    if (!open) return;
    place();
  }, [values.length, open, place]);

  // Second pass, once the popover exists to be measured. The first `place()`
  // runs before there is any box in the DOM, so it falls back to the floor
  // width; running it again here re-clamps against the real content width.
  //
  // `visible.length` is a dependency because filtering changes what is rendered
  // inside the max-content box and so can change its real width — typing a
  // filter that leaves only short names must be free to narrow the popover back
  // down, not keep the widest label's footprint for ever. There is no loop:
  // `place()` returns `prev` unchanged when nothing moved.
  useLayoutEffect(() => {
    if (!open || !popoverRef.current) return;
    place();
  }, [open, place, rect.minWidth, rect.maxWidth, rect.top, rect.flip, visible.length]);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node;
      // The whole wrapper counts as inside, not just the trigger: removing a
      // chip while the list is open must not close the list.
      if (popoverRef.current?.contains(target) || wrapperRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  // Clamped during render rather than reset in an effect: filtering can leave
  // the stored index past the end, and correcting it here avoids an extra
  // render pass and a highlight on nothing.
  const active = rowCount === 0 ? 0 : Math.min(activeIndex, rowCount - 1);
  /** Index 0 is the "add a new…" row when one is offered; the rest follow it. */
  const addNewIsActive = addNewRows === 1 && active === 0;
  /** Immediately under it, so the options start at `pinnedRows`. */
  const selectAllIsActive = selectAllRows === 1 && active === addNewRows;
  const activeOption = visible[active - pinnedRows];

  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  function close(returnFocus = true) {
    setOpen(false);
    setTerm('');
    if (returnFocus) triggerRef.current?.focus();
  }

  function toggle(option: MultiComboboxOption) {
    if (values.includes(option.value)) {
      onChange(values.filter((value) => value !== option.value));
      return;
    }
    if (atCap) return;
    onChange([...values, option.value]);
    // The term is deliberately kept: filtering to a surname and picking two
    // siblings is a real flow, and clearing it would undo the user's typing.
  }

  /**
   * The select-all row's whole behaviour: select every visible option, or — when
   * every one of them is already selected — deselect exactly those.
   *
   * Both halves preserve the order of `values`, which is the order the chips
   * render in and the only one the user can predict; new values are appended in
   * the order they appear on screen. The filter term is kept, for the same
   * reason `toggle` keeps it.
   */
  function toggleAllVisible() {
    if (allVisibleSelected) {
      // Exactly those, never the whole selection: values hidden by the filter
      // term were not on screen and are not this row's to remove.
      const drop = new Set(visible.map((option) => option.value));
      onChange(values.filter((value) => !drop.has(value)));
      return;
    }
    if (selectAllBlocked) return;
    const already = new Set(values);
    const added = visible
      .map((option) => option.value)
      .filter((value) => !already.has(value));
    onChange([...values, ...added]);
  }

  function remove(value: string) {
    onChange(values.filter((current) => current !== value));
    // The chip's own button is about to unmount, so park focus somewhere real
    // rather than letting it fall back to <body>.
    if (open) inputRef.current?.focus();
    else triggerRef.current?.focus();
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close();
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      /*
       * The one row that CLOSES on Enter. It selects nothing, so leaving the
       * list open behind whatever `onSelect` raises would leave a popover
       * sitting over it — and closing first is also what parks focus on the
       * trigger, which a dialog then remembers as where to hand focus back.
       */
      if (addNew && addNewIsActive) {
        if (atCap) return;
        close();
        addNew.onSelect();
        return;
      }
      /*
       * Toggles like an option and leaves the popover open, because it IS a
       * selection — the opposite of the row above it, which selects nothing and
       * therefore has to close.
       */
      if (selectAllIsActive) {
        toggleAllVisible();
        return;
      }
      const option = activeOption;
      // Enter toggles and the popover stays open — that is the whole point of
      // this control versus `Combobox`.
      if (option) toggle(option);
      return;
    }
    if (event.key === 'Backspace' && term === '' && values.length > 0) {
      // Only with an empty filter box, so it can never eat what you typed.
      event.preventDefault();
      const last = values[values.length - 1];
      if (last !== undefined) onChange(values.filter((value) => value !== last));
      return;
    }
    if (event.key === 'Tab') {
      // Close and land where the page's own tab order says, never back at the
      // start of the document (see `focusPastTrigger`).
      event.preventDefault();
      event.stopPropagation();
      close(false);
      if (!focusPastTrigger(triggerRef.current, popoverRef.current, event.shiftKey)) {
        triggerRef.current?.focus();
      }
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    // `rowCount`, not `visible.length`: the "add a new…" row is navigable, and
    // it is the ONLY row on a list that matched nothing.
    if (rowCount === 0) return;
    setActiveIndex((current) => {
      if (event.key === 'Home') return 0;
      if (event.key === 'End') return rowCount - 1;
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      return (current + delta + rowCount) % rowCount;
    });
  }

  // `<= 0` rather than `=== 0` so a caller cannot arrive at a half-state by
  // passing a negative: every value that lists no chips behaves the same way.
  const countOnly = maxVisibleChips <= 0;

  // Refused chips are shown even past the cap. Collapsing the exact thing the
  // server went out of its way to name into "+5 more" is the one case where the
  // cap costs more than it saves; the order of `values` is preserved either
  // way, so nothing jumps around.
  //
  // Count-only is the exception, and it has to be: there is no chip to mark, so
  // keeping the refused ones would render exactly the chips the caller asked
  // not to have. The signal is not dropped — it moves into the count, which
  // says how many were refused; see the summary badge below.
  const shownChips = useMemo(() => {
    if (countOnly) return [];
    if (chips.length <= maxVisibleChips) return chips;
    return chips.filter(
      (chip, index) => index < maxVisibleChips || (refused?.has(chip.value) ?? false),
    );
  }, [chips, countOnly, maxVisibleChips, refused]);
  const overflowCount = chips.length - shownChips.length;

  // Reads the caller's two forms; never derives one from the other.
  const countLabel = `${values.length} ${
    values.length === 1 ? summaryNoun.singular : summaryNoun.plural
  }`;

  return (
    <>
      {/*
       * The trigger is a button stretched over the whole box rather than a
       * button *containing* the chips: a <button> may not contain the chips'
       * own remove buttons. Chips sit above it and are pointer-events-none, so
       * clicking a chip's label still falls through and opens the list, while
       * its remove control re-enables pointer events for itself.
       */}
      <div
        ref={wrapperRef}
        className={cx(
          'relative flex min-h-9 w-full flex-wrap items-center gap-1 rounded-md border bg-surface p-1 pr-8 max-sm:min-h-11',
          // The chip gap below `sm` is load-bearing, not styling: it is the room
          // each remove button's enlarged hit area expands into. See the button
          // itself for the arithmetic — 10px here, and changing it shrinks the
          // touch targets or makes two of them overlap.
          'max-sm:gap-2.5',
          'transition-colors duration-150 ease-out motion-reduce:transition-none',
          isInvalid ? 'border-danger' : 'border-border',
          disabled ? 'cursor-not-allowed bg-surface-sunken' : 'hover:border-hairline-strong',
        )}
      >
        <button
          id={id}
          ref={triggerRef}
          type="button"
          disabled={disabled}
          onClick={() => setOpen((prev) => !prev)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown' || event.key === 'Enter') {
              event.preventDefault();
              setOpen(true);
            }
          }}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-invalid={isInvalid || undefined}
          // The cap note only exists while the popover is mounted, so only
          // point at it then — a dangling idref reads as nothing at all.
          aria-describedby={cx(describedBy, open && atCap && capId) || undefined}
          className="absolute inset-0 h-full w-full rounded-md disabled:cursor-not-allowed"
        >
          {/*
           * Each chip carries its own label, so the button names the count —
           * and, when a server has refused some, how many. The name is the one
           * string a screen reader is guaranteed to read on focus, which is why
           * the count lives here rather than only in a description. That
           * matters more in count-only mode, where there are no chip labels to
           * fall back on and this is the whole announcement.
           */}
          <span className="sr-only">
            {values.length === 0 ? placeholder : countLabel}
            {refusedCount > 0 && `, ${refusedCount} not accepted`}
          </span>
        </button>

        {chips.length === 0 && (
          <span className="pointer-events-none px-1.5 text-sm text-fg-subtle">{placeholder}</span>
        )}

        {shownChips.map((chip) => {
          const isRefused = refused?.has(chip.value) ?? false;
          return (
            <Badge
              key={chip.value}
              tone={isRefused ? 'danger' : 'neutral'}
              /* Leading glyph: the first signal, and the only one that survives
                 a greyscale screenshot or forced-colours mode. */
              icon={isRefused ? <AlertIcon /> : undefined}
              className={cx(
                'pointer-events-none relative max-w-full gap-1 pr-0.5',
                'transition-colors duration-150 ease-out motion-reduce:transition-none',
                // Second non-colour signal: the edge itself changes texture, so
                // the chip reads as different from its neighbours at a glance
                // even where the tone does not land.
                isRefused && 'border-dashed',
              )}
            >
              <span className="min-w-0 truncate">{chip.label}</span>
              {/*
               * Third signal, for a reader who never sees either of the other
               * two. Inside the chip rather than beside the control, because
               * "which of these was refused" is the question being answered.
               */}
              {isRefused && <span className="sr-only"> — not accepted</span>}
              {/* Name only on the hover label; the "not accepted" marker is
                  already carried by the chip's tone, glyph and aria-label. */}
              <Tooltip label={`Remove ${chip.label}`} describes={false} className="shrink-0">
              <button
                type="button"
                disabled={disabled}
                /*
                 * The remove control is the chip's only focusable node, so in a
                 * screen reader's focus mode its label is all that is read. It
                 * carries the marker too, or tabbing the chips would be the one
                 * path that cannot tell them apart.
                 */
                aria-label={
                  isRefused ? `Remove ${chip.label} — not accepted` : `Remove ${chip.label}`
                }
                onClick={() => remove(chip.value)}
                className={cx(
                  'pointer-events-auto relative grid h-4 w-4 shrink-0 place-items-center rounded-xs',
                  /*
                   * Touch target below `sm`. This was 20px, sitting between
                   * other 20px targets, and removing the right city on a phone
                   * was a coin flip — the quote grid's mobile card removes
                   * cities by tapping exactly these.
                   *
                   * 28px painted, and a transparent `::after` overlay taking the
                   * HIT area to 44x44 without the chip growing to match. The
                   * three numbers are one system and none of them is free:
                   *
                   *   button          28   (h-7; the glyph stays 12px)
                   *   chip            34   = 28 + Badge's py-0.5 + its border
                   *   wrapper gap-y   10   (max-sm:gap-2.5 on the wrapper)
                   *   row pitch       44   = 34 + 10
                   *   overlay         44   = 28 + 2 x 8 (-inset-2)
                   *
                   * Pitch EQUALS the overlay, so vertically stacked targets
                   * meet at the midpoint of the gap and never overlap. Two
                   * adjacent 44px targets that overlap are worse than two 20px
                   * ones — the tap is then ambiguous rather than merely small —
                   * so the gap cannot be tightened without shrinking this.
                   *
                   * Horizontally the overlay reaches 5px past the chip into a
                   * 10px gap, and the next chip's own remove button is at ITS
                   * far right, so no two overlays meet on that axis either.
                   * Leftwards it covers the Badge's 4px gap and ~4px of the
                   * label's tail — the label is `pointer-events-none` and
                   * belongs to this same chip, so nothing is stolen.
                   *
                   * Above `sm` the PAINTED box stays 16px with a 4px gap, so the
                   * layout is pixel-identical to before; a transparent
                   * `::after` takes the HIT area to 24x24 (WCAG 2.5.8 minimum,
                   * 2026-10-11). It reaches 2px past the chip's right edge into
                   * the 4px gap and ~1px past its top and bottom into the 4px
                   * row gap — the next chip's own button is at ITS far right,
                   * so no two overlays meet. Below `sm` the `max-sm:` overlay
                   * (later in the cascade) replaces it.
                   */
                  "after:absolute after:-inset-1 after:content-['']",
                  "max-sm:h-7 max-sm:w-7 max-sm:rounded-full max-sm:after:absolute max-sm:after:-inset-2 max-sm:after:content-['']",
                  'transition-colors duration-150 ease-out motion-reduce:transition-none',
                  isRefused
                    ? 'text-danger hover:bg-danger-border'
                    : 'text-fg-subtle hover:bg-surface-active hover:text-fg',
                  'disabled:pointer-events-none disabled:opacity-50',
                )}
              >
                <XIcon />
              </button>
              </Tooltip>
            </Badge>
          );
        })}

        {/*
         * Count-only: one badge standing for the whole selection. It carries
         * the refusal count itself, because with no chips to mark there is
         * otherwise nothing on the trigger saying that part of the selection
         * came back refused — the box's border and `aria-invalid` say only that
         * something is wrong, and the reader would have to open the popover to
         * find out how much. "4 cities · 2 refused" keeps the same promise the
         * marked chips make: the server named every offending id in one 422 so
         * the user can fix them all in one pass, and the trigger must not be
         * the place that information stops.
         */}
        {countOnly && chips.length > 0 && (
          <Badge
            tone={refusedCount > 0 ? 'danger' : 'neutral'}
            /* Same three-signal treatment a refused chip gets, so the count
               does not depend on the red either. */
            icon={refusedCount > 0 ? <AlertIcon /> : undefined}
            className={cx(
              'pointer-events-none relative max-w-full',
              'transition-colors duration-150 ease-out motion-reduce:transition-none',
              refusedCount > 0 && 'border-dashed',
            )}
          >
            <span className="min-w-0 truncate">
              {countLabel}
              {refusedCount > 0 && ` · ${refusedCount} refused`}
            </span>
          </Badge>
        )}

        {!countOnly && overflowCount > 0 && (
          <Badge
            tone="neutral"
            /*
             * This badge holds no remove button, so below `sm` it does not get
             * the 28px one that sets every other chip's height — without the
             * padding it would sit 12px shorter than the chips it stands among.
             * 16px line box + 2 x 8 + the border is the same 34px they are.
             */
            className="pointer-events-none relative max-sm:py-2"
          >
            +{overflowCount} more
          </Badge>
        )}

        <span className="pointer-events-none absolute top-1/2 right-2 -translate-y-1/2">
          <ChevronIcon />
        </span>
      </div>

      {open &&
        createPortal(
          <div
            ref={popoverRef}
            style={{
              position: 'fixed',
              // `width: max-content` lets the box size to whichever option
              // label is longest; `minWidth`/`maxWidth` are the two bounds
              // `place()` computed (the existing floor, and the viewport);
              // `left` is corrected against the REAL resulting width by the
              // measure effect above, since `place()` alone only ever knows
              // the floor. All four are recomputed together on scroll, on
              // resize and when the chips rewrap.
              left: rect.left,
              minWidth: rect.minWidth,
              maxWidth: rect.maxWidth,
              width: 'max-content',
              ...(rect.flip
                ? { bottom: window.innerHeight - rect.top + 4 }
                : { top: rect.top + 4 }),
            }}
            className="z-[60] overflow-hidden rounded-lg border border-border bg-surface-raised shadow-md"
          >
            <div className="flex items-center gap-2 border-b border-border px-2.5 py-2">
              <SearchIcon />
              <input
                ref={inputRef}
                type="text"
                role="combobox"
                aria-expanded="true"
                aria-controls={listId}
                aria-autocomplete="list"
                aria-activedescendant={rowCount > 0 ? `${listId}-${active}` : undefined}
                value={term}
                onChange={(event) => {
                  setTerm(event.target.value);
                  setActiveIndex(0);
                }}
                onKeyDown={onKeyDown}
                placeholder={searchPlaceholder}
                className="min-w-0 flex-1 bg-transparent text-sm text-fg outline-none placeholder:text-fg-subtle"
              />
            </div>

            <ul
              id={listId}
              ref={listRef}
              role="listbox"
              aria-multiselectable="true"
              style={{ maxHeight: POPOVER_MAX_HEIGHT }}
              className="m-0 list-none overflow-y-auto p-1"
            >
              {/*
                * Pinned first, above the filter's results and above "Loading…",
                * because it is the row somebody reaches for when the list does
                * NOT contain what they want — which is exactly the moment they
                * have typed a term that matches nothing.
                *
                * `role="option"` and the shared `data-active` keep it inside the
                * listbox's own navigation rather than beside it; it is index 0,
                * and everything below it carries `pinnedRows` added to its index.
                */}
              {addNew && (
                <li>
                  <div
                    id={`${listId}-0`}
                    role="option"
                    aria-selected={false}
                    aria-disabled={atCap || undefined}
                    data-active={addNewIsActive}
                    onPointerDown={(event) => {
                      // Before the input's blur can close the popover, as below.
                      event.preventDefault();
                      if (atCap) return;
                      close();
                      addNew.onSelect();
                    }}
                    onMouseEnter={() => setActiveIndex(0)}
                    className={cx(
                      'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium',
                      atCap ? 'cursor-not-allowed text-fg-muted opacity-60' : 'cursor-pointer text-fg',
                      !atCap && addNewIsActive && 'bg-surface-hover',
                    )}
                  >
                    {/*
                     * A SPACER, never a checkbox: this row selects nothing, so
                     * a box beside it would offer a state it does not have.
                     * Without it the label would sit 24px left of every option
                     * label below and the list would read as ragged.
                     */}
                    <RowCheckbox />
                    {/* The wording carries the meaning. Neutral ink: the accent
                        is reserved for primary actions, active nav and focus. */}
                    <span className="min-w-0 flex-1 truncate">{addNew.label}</span>
                  </div>
                </li>
              )}

              {/*
                * The select-all row — pinned UNDER "add a new…" and above the
                * options, so that row keeps index 0 and the id its own doc
                * records.
                *
                * A ROW rather than a button in the popover header, for exactly
                * the reason the row above it is one: `Tab` inside the popover
                * closes it, so anything outside the listbox would be
                * pointer-only and no keyboard could ever reach it.
                *
                * Not an accent action — the accent is reserved for "add a
                * new…", which creates something; this only ticks what is
                * already here.
                */}
              {selectAllRows === 1 && (
                <li>
                  <div
                    id={`${listId}-${addNewRows}`}
                    role="option"
                    /* It is a control, not a selection of its own; the options
                       below carry the selected state. */
                    aria-selected={false}
                    aria-disabled={selectAllBlocked || undefined}
                    title={selectAllBlockedReason}
                    data-active={selectAllIsActive}
                    onPointerDown={(event) => {
                      // Before the input's blur can close the popover, as below.
                      event.preventDefault();
                      toggleAllVisible();
                    }}
                    onMouseEnter={() => setActiveIndex(addNewRows)}
                    className={cx(
                      'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium',
                      selectAllBlocked
                        ? 'cursor-not-allowed text-fg-muted opacity-60'
                        : 'cursor-pointer',
                      !selectAllBlocked &&
                        (selectAllIsActive ? 'bg-surface-hover text-fg' : 'text-fg-secondary'),
                    )}
                  >
                    {/* Spacer, on the same reasoning as the row above. */}
                    <RowCheckbox />
                    {/* The ONE copy of this wording. Deliberately not a caller
                        prop: thirty mounts would word one control thirty
                        ways. */}
                    <span className="min-w-0 flex-1 truncate">
                      {allVisibleSelected ? 'Clear all' : 'Select all'}
                    </span>
                    {/* The refusal reaches a screen reader as well as a hover,
                        the same pair a refused option row uses below. */}
                    {selectAllBlockedReason && (
                      <span className="sr-only"> — {selectAllBlockedReason}</span>
                    )}
                  </div>
                </li>
              )}

              {loading && <li className="px-2 py-3 text-center text-xs text-fg-muted">Loading…</li>}

              {!loading && visible.length === 0 && (
                <li className="px-2 py-3 text-center text-xs text-fg-muted">
                  {term.trim() === '' ? 'Nothing to choose from' : `No matches for “${term.trim()}”`}
                </li>
              )}

              {!loading &&
                visible.map((option, index) => {
                  const isSelected = values.includes(option.value);
                  const isRefused = isSelected && (refused?.has(option.value) ?? false);
                  // Shifted by whichever pinned rows are above the options.
                  const rowIndex = index + pinnedRows;
                  const isActive = rowIndex === active;
                  // At the cap only the selected rows stay live, so the user can
                  // still swap one out for another.
                  const isBlocked = atCap && !isSelected;
                  return (
                    <li key={option.value}>
                      <div
                        id={`${listId}-${rowIndex}`}
                        role="option"
                        aria-selected={isSelected}
                        aria-disabled={isBlocked || undefined}
                        data-active={isActive}
                        onPointerDown={(event) => {
                          // Toggle before the input's blur can close the popover.
                          event.preventDefault();
                          if (isBlocked) return;
                          toggle(option);
                        }}
                        onMouseEnter={() => setActiveIndex(rowIndex)}
                        className={cx(
                          'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm',
                          isBlocked ? 'cursor-not-allowed text-fg-muted opacity-60' : 'cursor-pointer',
                          !isBlocked && (isActive ? 'bg-surface-hover text-fg' : 'text-fg-secondary'),
                        )}
                      >
                        {/*
                         * The LEADING mark, matching the filter popover's own
                         * option rows (`ColumnFilter`) rather than inventing a
                         * second look for the same idea. It is the box, not a
                         * colour, that marks a row chosen — the bold label
                         * beside it is a second signal, never the only one.
                         *
                         * The indent works out identical to that popover's:
                         * `px-2` + 16px box + `gap-2` here, `px-1.5` + 16px +
                         * `Checkbox`'s own `gap-2.5` there. Move either and the
                         * two lists stop lining up.
                         */}
                        <RowCheckbox checked={isSelected} />
                        <span className="min-w-0 flex-1">
                          <span className={cx('block truncate', isSelected && 'font-medium text-fg')}>
                            {option.label}
                          </span>
                          {option.hint && (
                            <span className="block truncate text-xs text-fg-muted">
                              {option.hint}
                            </span>
                          )}
                        </span>
                        {/*
                         * A refused row keeps its checkbox TICKED — it IS still
                         * selected, and unticking it would say otherwise.
                         *
                         * ⚠️ The glyph and its hidden text stay at the END of
                         * the row, where they are now the ONLY thing: the
                         * selected mark used to sit beside them and has moved
                         * to the start of the row. So this is no longer a
                         * second mark added next to a tick — it is the whole of
                         * the row's trailing content, and the two signals are
                         * now at opposite ends. Either alone still identifies
                         * the row the user came here to swap out, from the list
                         * as well as from the chip.
                         */}
                        {isRefused && (
                          <>
                            <AlertIcon className="h-3.5 w-3.5 text-danger" />
                            <span className="sr-only">Not accepted</span>
                          </>
                        )}
                      </div>
                    </li>
                  );
                })}
            </ul>

            {atCap && (
              <p
                id={capId}
                role="status"
                className="border-t border-warning-border bg-warning-soft px-2.5 py-1.5 text-2xs text-warning"
              >
                Up to {maxSelected} can be selected. Remove one to add another.
              </p>
            )}

            {footnote && (
              <p className="border-t border-border px-2.5 py-1.5 text-2xs text-fg-muted">
                {footnote}
              </p>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
